import { c as _c } from "react/compiler-runtime";
import { MessageCircle, Paperclip, Phone } from 'lucide-react';
import type { BlocoDocumento } from '@/shared/components/documentos/paginado';
import { ChatMessageOptions } from '@/shared/components/ui/common/ChatMessageOptions';
import { OutboundStatusIcon } from '@/shared/components/ui/common/OutboundStatusIcon';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import type { ConversaDoCertificado, FatiaConversaCertificadoProps, FatiaTabelaCertificadoProps, TabelaCertificadoInput } from '../../../types/certificadoNegociacaoTypes';
import { fatiarMensagemDoCertificado } from '../../../utils/certificadoNegociacao';
import { dataDoMarco, horaDoMarco } from '../../../utils/marcoDataHora';
function FatiaTabela(t0) {
  const $ = _c(16);
  const {
    cabecalhos,
    colunas,
    linhas,
    indice,
    primeiroDaPagina
  } = t0;
  const abre = indice === 0 || primeiroDaPagina;
  const ultima = indice === linhas.length - 1;
  const t1 = `px-[37px] ${ultima ? "pb-[26px]" : ""}`;
  const t2 = `overflow-hidden border-x border-solid border-neutral-200 ${abre ? "rounded-t-[8px] border-t" : ""} ${ultima ? "rounded-b-[8px] border-b" : ""}`;
  let t3;
  if ($[0] !== abre || $[1] !== cabecalhos || $[2] !== colunas) {
    t3 = abre && <div className={`grid ${colunas} border-b border-neutral-200 bg-neutral-100 px-[14px] py-[9px] text-[11px] font-semibold tracking-[0.5px] text-neutral-500 uppercase`}>{cabecalhos.map(_temp)}</div>;
    $[0] = abre;
    $[1] = cabecalhos;
    $[2] = colunas;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `grid ${colunas} items-start gap-x-3 px-[14px] py-[12px] ${ultima ? "" : "border-b border-neutral-100"}`;
  const t5 = linhas[indice];
  let t6;
  if ($[4] !== t5.celulas) {
    t6 = t5.celulas.map(_temp2);
    $[4] = t5.celulas;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t4 || $[7] !== t6) {
    t7 = <div className={t4}>{t6}</div>;
    $[6] = t4;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t7) {
    t8 = <div className={t2}>{t3}{t7}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t1 || $[14] !== t8) {
    t9 = <div className={t1}>{t8}</div>;
    $[13] = t1;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp2(celula, index) {
  return <div key={index} className={`min-w-0 text-[12.5px] leading-[1.6] wrap-anywhere whitespace-pre-wrap ${index === 0 ? "text-brand-600 font-medium" : "text-neutral-700"}`}>{celula}</div>;
}
function _temp(cabecalho) {
  return <div key={cabecalho}>{cabecalho}</div>;
}
export function blocosTabelaCertificado(tabela: TabelaCertificadoInput): BlocoDocumento[] {
  return tabela.linhas.map((linha, indice) => ({
    id: `${tabela.id}-${linha.id}`,
    render: ({
      primeiroDaPagina
    }) => <FatiaTabela {...tabela} indice={indice} primeiroDaPagina={primeiroDaPagina} />
  }));
}
function FatiaConversa(t0) {
  const $ = _c(55);
  const {
    conversa,
    mensagem,
    parte,
    primeira,
    ultima,
    primeiroDaPagina,
    continuacao,
    mostrarData,
    opcoes
  } = t0;
  const recebida = mensagem.direcao === "INBOUND";
  const abre = primeira || primeiroDaPagina;
  const t1 = `px-[37px] ${ultima ? "pb-[26px]" : ""}`;
  const t2 = `overflow-hidden border-x border-neutral-200 ${abre ? "rounded-t-[10px] border-t" : ""} ${ultima ? "rounded-b-[10px] border-b" : ""}`;
  let t3;
  if ($[0] !== abre || $[1] !== conversa || $[2] !== primeira) {
    t3 = abre && <div className="flex items-center gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3"><div className="bg-brand-600/10 text-brand-600 flex size-9 shrink-0 items-center justify-center rounded-full"><Phone className="size-4" /></div><div><p className="text-[13px] font-semibold text-neutral-800">{conversa.numero ? formatTelefoneComCodigoPais(conversa.numero) : "N\xFAmero n\xE3o informado"}</p><p className="text-[11px] text-neutral-500">WhatsApp · {conversa.totalDeMensagens}{" "}{conversa.totalDeMensagens === 1 ? "mensagem" : "mensagens"}{!primeira ? " \xB7 continua\xE7\xE3o da conversa" : ""}</p></div></div>;
    $[0] = abre;
    $[1] = conversa;
    $[2] = primeira;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `flex flex-col px-4 ${abre ? "pt-4" : "pt-1.5"} ${ultima ? "pb-4" : "pb-1.5"} bg-[var(--bg-whatsapp-chat)]`;
  let t5;
  if ($[4] !== mensagem.dataHora || $[5] !== mostrarData) {
    t5 = mostrarData && <div className="mb-3 flex justify-center"><span className="rounded-[8px] bg-white/80 px-3 py-1 text-[11px] text-neutral-500">{dataDoMarco(mensagem.dataHora)}</span></div>;
    $[4] = mensagem.dataHora;
    $[5] = mostrarData;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `flex ${recebida ? "justify-start" : "justify-end"}`;
  const t7 = `flex max-w-[85%] flex-col rounded-[8px] px-3 py-2 shadow-sm ${recebida ? "bg-white" : "bg-success-50"}`;
  let t8;
  if ($[7] !== continuacao || $[8] !== mensagem.autoriaRotulo) {
    t8 = !continuacao && <p className="text-brand-700 mb-1 text-[11px] font-semibold">{mensagem.autoriaRotulo}</p>;
    $[7] = continuacao;
    $[8] = mensagem.autoriaRotulo;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== continuacao) {
    t9 = continuacao && <p className="mb-1 text-[10px] text-neutral-500">Continuação da mensagem</p>;
    $[10] = continuacao;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== opcoes || $[13] !== parte) {
    t10 = parte || !opcoes ? <p data-testid="certificado-mensagem-texto" className="text-[12.5px] leading-[1.6] wrap-anywhere whitespace-pre-wrap text-neutral-900">{parte}</p> : null;
    $[12] = opcoes;
    $[13] = parte;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== continuacao || $[16] !== mensagem.midia) {
    t11 = mensagem.midia && !continuacao && <p className="text-brand-700 mt-2 flex items-center gap-2 text-[12px]"><Paperclip className="size-4" />{mensagem.midia.rotulo}{mensagem.midia.referencia ? ` · ${mensagem.midia.referencia}` : ""}</p>;
    $[15] = continuacao;
    $[16] = mensagem.midia;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== opcoes) {
    t12 = opcoes ? <ChatMessageOptions choices={opcoes} /> : null;
    $[18] = opcoes;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== mensagem.dataHora) {
    t13 = horaDoMarco(mensagem.dataHora);
    $[20] = mensagem.dataHora;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = <span className="text-[10.5px] text-neutral-500">{t13}</span>;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== mensagem.status || $[25] !== recebida) {
    t15 = !recebida && <OutboundStatusIcon status={mensagem.status} onLight={true} size={14} />;
    $[24] = mensagem.status;
    $[25] = recebida;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = <div className="mt-1 flex items-center justify-end gap-1">{t14}{t15}</div>;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== mensagem.status) {
    t17 = mensagem.status === "FAILED" && <p className="text-error-600 mt-1 text-[11px]">Falha no envio · sem comprovação de entrega</p>;
    $[30] = mensagem.status;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t16 || $[36] !== t17 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t18 = <div className={t7}>{t8}{t9}{t10}{t11}{t12}{t16}{t17}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t16;
    $[36] = t17;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== t6) {
    t19 = <div className={t6}>{t18}</div>;
    $[41] = t18;
    $[42] = t6;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19 || $[45] !== t4 || $[46] !== t5) {
    t20 = <div className={t4}>{t5}{t19}</div>;
    $[44] = t19;
    $[45] = t4;
    $[46] = t5;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t2 || $[49] !== t20 || $[50] !== t3) {
    t21 = <div className={t2}>{t3}{t20}</div>;
    $[48] = t2;
    $[49] = t20;
    $[50] = t3;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t1 || $[53] !== t21) {
    t22 = <div className={t1}>{t21}</div>;
    $[52] = t1;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}

/**
 * "Conversa N" e "K mensagens" vêm do bloco congelado, e não do que já chegou: um bloco que
 * atravessa páginas mostra o total dele desde a primeira. O fim do bloco (borda de baixo) só
 * fecha quando a última mensagem dele chegou.
 */
export function blocosConversasCertificado(conversas: ConversaDoCertificado[], secaoCompleta: boolean): BlocoDocumento[] {
  return conversas.flatMap(conversa => {
    const blocoInteiro = secaoCompleta || conversa.mensagens.length >= conversa.totalDeMensagens;
    const fatias = conversa.mensagens.flatMap((mensagem, indiceMensagem) => fatiarMensagemDoCertificado(mensagem).map((fatia, indiceParte) => ({
      mensagem,
      ...fatia,
      mostrarData: indiceParte === 0 && (indiceMensagem === 0 || dataDoMarco(conversa.mensagens[indiceMensagem - 1].dataHora) !== dataDoMarco(mensagem.dataHora))
    })));
    return [{
      id: `conversa-titulo-${conversa.indice}`,
      manterComProximo: true,
      node: <div className="px-[37px] pt-2 pb-4">
            <div className="text-brand-600 mb-2 flex items-center gap-2">
              <MessageCircle className="size-4" />
              <h3 className="text-[12px] font-bold tracking-[0.6px]">Conversa {conversa.indice}</h3>
            </div>
            <div className="h-px bg-neutral-200" />
          </div>
    }, ...fatias.map((fatia, indice): BlocoDocumento => ({
      id: `conversa-${conversa.indice}-fatia-${indice}`,
      render: ({
        primeiroDaPagina
      }) => <FatiaConversa {...fatia} conversa={conversa} primeira={indice === 0} ultima={blocoInteiro && indice === fatias.length - 1} primeiroDaPagina={primeiroDaPagina} />
    }))];
  });
}